import { useState, useEffect, useRef, useCallback } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import MagnumSays from '@/components/MagnumSays'
import ScreenTimeLaunchModal from '@/components/ScreenTimeLaunchModal'

interface TvInfo {
  id: number
  name: string
  tvType: string
  mode: string
  hasActiveSession: boolean
  periodType?: string
  activeSession?: {
    id: number
    startedAt: number
    expiresAt: number
    minutesRequested: number
  }
}

interface ScheduleWindow {
  dayOfWeek: number
  startMinute: number
  endMinute: number
  periodType: string
}

interface NextWindow extends ScheduleWindow {
  startsAt: number
}

interface ScheduleStatus {
  todayIso: number
  nowMinute: number
  currentPeriodType: string
  allowedNow: boolean
  nextWindow: NextWindow | null
  windows: ScheduleWindow[]
}

interface ScreenTimeData {
  balance: number
  bonusBalance: number
  budget: number | null
  isConfigured: boolean
  isUnlimited: boolean
  earnedToday: number
  redeemedToday: number
  tvs: TvInfo[]
  schedule?: ScheduleStatus | null
  timezone?: string
  signoffRequired?: boolean
  signoffApproved?: boolean
}

const PRESETS = [15, 30, 45, 60]
const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const PERIOD_META: Record<string, { label: string; emoji: string; cls: string }> = {
  UNLIMITED: { label: 'Free TV', emoji: '🎉', cls: 'text-green-600' },
  DAILY_ALLOWANCE: { label: 'Your minutes', emoji: '⏳', cls: 'text-primary' },
  BONUS_ONLY: { label: 'Bonus only', emoji: '⭐', cls: 'text-amber-600' },
}

function fmtTime(min: number): string {
  const h24 = Math.floor(min / 60) % 24
  const m = min % 60
  const period = h24 < 12 ? 'AM' : 'PM'
  let hr = h24 % 12
  if (hr === 0) hr = 12
  return m === 0 ? `${hr} ${period}` : `${hr}:${String(m).padStart(2, '0')} ${period}`
}

function relDay(iso: number, todayIso: number): string {
  if (iso === todayIso) return 'Today'
  if (iso === (todayIso % 7) + 1) return 'Tomorrow'
  return DAY_NAMES[iso]
}

function WeeklySchedule({ schedule }: { schedule: ScheduleStatus }) {
  const byDay: Record<number, ScheduleWindow[]> = {}
  for (const w of schedule.windows) {
    if (w.periodType === 'NEVER') continue
    ;(byDay[w.dayOfWeek] ||= []).push(w)
  }

  return (
    <div className="rounded-2xl border-2 border-border p-3 space-y-1.5 bg-background">
      {[1, 2, 3, 4, 5, 6, 7].map((iso) => {
        const wins = (byDay[iso] || []).sort((a, b) => a.startMinute - b.startMinute)
        const isToday = iso === schedule.todayIso
        return (
          <div key={iso} className={`flex items-start gap-2 text-xs ${isToday ? 'font-semibold' : ''}`}>
            <span className={`w-12 shrink-0 ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
              {DAY_NAMES[iso]}
              {isToday ? ' •' : ''}
            </span>
            <div className="flex-1 flex flex-wrap gap-x-3 gap-y-1">
              {wins.length === 0 ? (
                <span className="text-muted-foreground">No screen time</span>
              ) : (
                wins.map((w, i) => {
                  const meta = PERIOD_META[w.periodType]
                  return (
                    <span key={i} className="inline-flex items-center gap-1 whitespace-nowrap">
                      <span>{meta?.emoji}</span>
                      <span>
                        {fmtTime(w.startMinute)}–{fmtTime(w.endMinute)}
                      </span>
                      {meta && <span className={meta.cls}>{meta.label}</span>}
                    </span>
                  )
                })
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function ScreenTimeCard() {
  const [data, setData] = useState<ScreenTimeData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedTv, setSelectedTv] = useState<number | null>(null)
  const [minutes, setMinutes] = useState(30)
  // Minutes the kid has committed to, waiting on the red button. Null = closed.
  const [armedMinutes, setArmedMinutes] = useState<number | null>(null)
  const [remaining, setRemaining] = useState(0)
  const [showSchedule, setShowSchedule] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval>>()

  const load = async () => {
    try {
      const result = await apiFetch<ScreenTimeData>('/screen-time/mine')
      setData(result)
      // Default to the first TV so the redeem button is actionable; keep the
      // kid's pick if they've already chosen one that still exists.
      if (result.tvs.length > 0) {
        setSelectedTv((prev) =>
          prev != null && result.tvs.some((t) => t.id === prev) ? prev : result.tvs[0].id,
        )
      }
      // Keep the picked duration inside what's left, so the highlighted preset
      // matches what the button will actually redeem.
      if (!result.isUnlimited && result.balance > 0) {
        setMinutes((prev) => Math.min(prev, result.balance))
      }
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const activeTv = data?.tvs.find((t) => t.hasActiveSession)

  useEffect(() => {
    if (activeTv?.activeSession) {
      const update = () => {
        const left = Math.max(0, Math.ceil((activeTv.activeSession!.expiresAt - Date.now()) / 60000))
        setRemaining(left)
      }
      update()
      timerRef.current = setInterval(update, 15000)
      return () => clearInterval(timerRef.current)
    }
  }, [activeTv?.activeSession?.id])

  // Redeems the amount the button actually offers. Sending the raw `minutes`
  // state instead meant that once the balance dropped below the last preset the
  // server rejected every request as over-balance — and the error was swallowed,
  // so tapping the button just silently did nothing for the rest of the day.
  //
  // The call itself is deferred to the launch modal's red button: choosing an
  // amount arms it, pressing the button opens the stream. Nothing is spent
  // until then, so backing out of the modal costs the kid nothing.
  const redeem = async (requested: number) => {
    if (!selectedTv || requested <= 0) return
    await apiFetch('/screen-time/mine/redeem', {
      method: 'POST',
      body: JSON.stringify({ tvId: selectedTv, minutes: requested }),
    })
  }

  const closeLaunch = useCallback(() => {
    setArmedMinutes(null)
    load()
  }, [])

  const [busy, setBusy] = useState(false)
  const [sessionError, setSessionError] = useState<string | null>(null)

  const extendSession = async (sessionId: number, extra: number) => {
    setBusy(true)
    setSessionError(null)
    try {
      await apiFetch(`/screen-time/mine/sessions/${sessionId}/extend`, {
        method: 'POST',
        body: JSON.stringify({ minutes: extra }),
      })
      await load()
    } catch (err) {
      setSessionError(err instanceof Error ? err.message : "That didn't work — try again.")
    } finally {
      setBusy(false)
    }
  }

  const cancelSession = async (sessionId: number) => {
    setBusy(true)
    setSessionError(null)
    try {
      await apiFetch(`/screen-time/mine/sessions/${sessionId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({}),
      })
      await load()
    } catch (err) {
      setSessionError(err instanceof Error ? err.message : "That didn't work — try again.")
    } finally {
      setBusy(false)
    }
  }

  if (loading) return null
  if (!data || data.tvs.length === 0) return null

  const schedule = data.schedule ?? null
  const isUnlimited = data.isUnlimited

  // Whether the kid could turn a TV on right now (ignores schedule entirely for unlimited kids).
  const tvWatchableNow = (tv: TvInfo): boolean => {
    if (isUnlimited) return tv.mode !== 'DENY'
    if (tv.mode === 'ALLOW') return true
    if (tv.mode === 'DENY') return false
    if (tv.mode === 'SCHEDULE') {
      if (tv.periodType === 'UNLIMITED') return true
      if (tv.periodType === 'DAILY_ALLOWANCE') return data.balance > 0
      if (tv.periodType === 'BONUS_ONLY') return data.bonusBalance > 0
      return false // NEVER / outside hours
    }
    return data.balance > 0 // legacy, no schedule
  }

  const freeNow = isUnlimited || data.tvs.some((t) => t.periodType === 'UNLIMITED' || t.mode === 'ALLOW')
  const watchableNow = data.tvs.some(tvWatchableNow)
  // Homework check: a parent still needs to sign off before minutes can be redeemed today.
  const needsSignoff = !isUnlimited && data.signoffRequired === true && data.signoffApproved !== true

  // ---- Status headline (Magnus voice) ----
  let headline: JSX.Element
  if (activeTv?.activeSession) {
    headline = (
      <MagnumSays variant="success" size="sm">
        {activeTv.name} is on — you've got {remaining} minute{remaining === 1 ? '' : 's'} left. Enjoy! 🎉
      </MagnumSays>
    )
  } else if (isUnlimited) {
    headline = (
      <MagnumSays variant="success" size="sm">
        You've got unlimited screen time — watch whenever you like! Pick a TV below to turn it on.
      </MagnumSays>
    )
  } else if (freeNow) {
    headline = (
      <MagnumSays variant="success" size="sm">
        It's free TV time right now — no minutes needed. Go for it! 🎉
      </MagnumSays>
    )
  } else if (needsSignoff) {
    headline = (
      <MagnumSays size="sm">
        Homework check! 📝 Show a parent your finished homework and have them tap approve —
        then your minutes are all yours.
      </MagnumSays>
    )
  } else if (watchableNow) {
    headline = (
      <MagnumSays variant="success" size="sm">
        Screen time's open! Pick a TV and how long below. 📺
      </MagnumSays>
    )
  } else {
    // Closed — explain why and (when it's a schedule thing) when it opens next.
    const outsideHours = schedule != null && !schedule.allowedNow
    const outOfMinutes = data.balance === 0
    let why: string
    let next: NextWindow | null = null
    if (outsideHours) {
      why = "It's outside screen-time hours right now."
      next = schedule!.nextWindow
    } else if (outOfMinutes) {
      why = "You're all out of minutes for now. Finish a reading goal to earn more! 📚"
    } else {
      why = 'Screen time is off right now. Check with your parent if that’s a surprise!'
    }
    headline = (
      <MagnumSays size="sm">
        {why}
        {outsideHours && next && (
          <span className="block mt-1 font-medium">
            Next screen time: {relDay(next.dayOfWeek, schedule!.todayIso)} at {fmtTime(next.startMinute)}.
          </span>
        )}
      </MagnumSays>
    )
  }

  // ---- Redeem controls (skip during active session and free/unlimited windows) ----
  const selectedTvInfo = data.tvs.find((t) => t.id === selectedTv)
  const currentPeriod = selectedTvInfo?.periodType
  const isBonusOnly = !isUnlimited && currentPeriod === 'BONUS_ONLY'
  const availableBalance = isBonusOnly ? data.bonusBalance : data.balance

  // For unlimited kids the schedule never blocks redemption.
  const periodBlocksRedeem =
    !isUnlimited && (currentPeriod === 'UNLIMITED' || currentPeriod === 'NEVER')
  const noBalance = !isUnlimited && availableBalance === 0
  const showRedeem = !activeTv && !needsSignoff && !periodBlocksRedeem && !noBalance && (isUnlimited || data.isConfigured || data.balance > 0)

  const effectiveMinutes = isUnlimited ? minutes : Math.min(minutes, availableBalance)

  return (
    <div className="space-y-3">
      {headline}

      {activeTv?.activeSession && (
        <div className="border-2 border-green-300 rounded-2xl p-4 bg-green-50/60 space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-3xl">📺</span>
            <div>
              <p className="font-semibold text-sm">{activeTv.name} is on</p>
              <p className="text-xs text-muted-foreground">
                <span className="font-bold text-green-700">
                  {remaining} minute{remaining === 1 ? '' : 's'}
                </span>{' '}
                left of {activeTv.activeSession.minutesRequested}
              </p>
            </div>
          </div>

          {(isUnlimited || data.balance > 0) && (
            <div>
              <p className="text-xs font-medium mb-1">Need more time?</p>
              <div className="flex gap-1 flex-wrap">
                {[15, 30].filter((m) => isUnlimited || m <= data.balance).map((m) => (
                  <button
                    key={m}
                    disabled={busy}
                    onClick={() => extendSession(activeTv.activeSession!.id, m)}
                    className="px-3 py-1.5 rounded-lg border border-green-400 text-green-800 text-xs font-medium transition hover:bg-green-100 disabled:opacity-50 min-h-[36px]"
                  >
                    +{m} min
                  </button>
                ))}
                {!isUnlimited && data.balance > 0 && data.balance < 15 && (
                  <button
                    disabled={busy}
                    onClick={() => extendSession(activeTv.activeSession!.id, data.balance)}
                    className="px-3 py-1.5 rounded-lg border border-green-400 text-green-800 text-xs font-medium transition hover:bg-green-100 disabled:opacity-50 min-h-[36px]"
                  >
                    +{data.balance} min (all)
                  </button>
                )}
              </div>
              {!isUnlimited && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  {data.balance} min still in the bank
                </p>
              )}
            </div>
          )}

          {/* Stopping early hands the unwatched minutes back, so it is worth doing. */}
          <button
            disabled={busy}
            onClick={() => cancelSession(activeTv.activeSession!.id)}
            className="w-full py-2 rounded-xl border-2 border-border text-sm font-semibold transition hover:bg-accent disabled:opacity-50 min-h-[44px]"
          >
            I'm done — keep my {remaining} min
          </button>

          {sessionError && (
            <MagnumSays variant="warning" size="sm">{sessionError}</MagnumSays>
          )}
        </div>
      )}

      {showRedeem && (
        <div className="border-2 border-primary/30 rounded-2xl p-4 bg-primary/5 space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-3xl">📺</span>
            <div>
              <p className="font-semibold text-sm">Watch TV</p>
              {isUnlimited ? (
                <p className="text-xs text-muted-foreground">
                  <span className="font-bold text-primary">Unlimited</span> — pick how long you want
                </p>
              ) : isBonusOnly ? (
                <p className="text-xs text-muted-foreground">
                  <span className="font-bold text-amber-600">{data.bonusBalance} min</span> bonus time available
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  <span className="font-bold text-primary">{data.balance} min</span> remaining today
                  {data.budget != null && data.budget > 0 && (
                    <span className="text-muted-foreground"> of {data.budget + data.earnedToday}</span>
                  )}
                </p>
              )}
            </div>
          </div>

          {data.tvs.length > 1 && (
            <div>
              <p className="text-xs font-medium mb-1">Which TV?</p>
              <div className="flex gap-1 flex-wrap">
                {data.tvs.map((tv) => (
                  <button
                    key={tv.id}
                    onClick={() => setSelectedTv(tv.id)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition min-h-[36px] ${
                      selectedTv === tv.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground'
                    }`}
                  >
                    {tv.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-medium mb-1">How long?</p>
            <div className="flex gap-1 flex-wrap">
              {PRESETS.filter((p) => isUnlimited || p <= availableBalance).map((p) => (
                <button
                  key={p}
                  onClick={() => setMinutes(p)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition min-h-[36px] ${
                    minutes === p
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground'
                  }`}
                >
                  {p} min
                </button>
              ))}
              {!isUnlimited && !PRESETS.includes(availableBalance) && availableBalance > 0 && (
                <button
                  onClick={() => setMinutes(availableBalance)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition min-h-[36px] ${
                    minutes === availableBalance
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground'
                  }`}
                >
                  All ({availableBalance} min)
                </button>
              )}
            </div>
          </div>

          <button
            onClick={() => setArmedMinutes(effectiveMinutes)}
            disabled={!selectedTv || effectiveMinutes <= 0}
            className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-bold hover:opacity-90 transition disabled:opacity-50 min-h-[44px]"
          >
            Watch TV for {effectiveMinutes} min
          </button>
        </div>
      )}

      {schedule && (
        <div>
          <button
            onClick={() => setShowSchedule((s) => !s)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-xl border border-border text-xs font-medium text-muted-foreground hover:bg-accent transition min-h-[40px]"
          >
            <span>📅 This week's screen-time schedule</span>
            <span>{showSchedule ? '▲' : '▼'}</span>
          </button>
          {showSchedule && (
            <div className="mt-2">
              <WeeklySchedule schedule={schedule} />
            </div>
          )}
        </div>
      )}

      {armedMinutes != null && (
        <ScreenTimeLaunchModal
          minutes={armedMinutes}
          tvName={selectedTvInfo?.name ?? 'the TV'}
          onLaunch={() => redeem(armedMinutes)}
          onClose={closeLaunch}
        />
      )}
    </div>
  )
}
